import { useState } from 'react';
import { TarotCard } from './TarotCard';
import { CelticCrossLayout } from './CelticCrossLayout';
import { Button, Loading } from '../common';
import type { TarotSpread, DrawnCard, TarotCard as TarotCardType } from '../../types/tarot';
import { drawCards } from '../../utils/tarotLogic';
import { useEquippedDecorations } from '../../hooks/useDecorations';
import { getResponsiveBackgroundUrl } from '../../utils/decorationAssets';
import '../../styles/tarot-animations.css';

interface CardDrawingProps {
  spread: TarotSpread;
  deck: TarotCardType[];
  onDrawingComplete: (cards: DrawnCard[]) => void;
  onBack: () => void;
}

// Helper to convert deck name to deck ID
function getDeckIdFromName(deckName: string | null): string | null {
  if (!deckName) return null;
  const nameLower = deckName.toLowerCase();
  if (nameLower.includes('celestial wisdom') || nameLower.includes('celestial-wisdom')) {
    return 'celestial-wisdom';
  }
  if (nameLower.includes('mystic dreams') || nameLower.includes('mystic-dreams')) {
    return 'mystic-dreams';
  }
  if (nameLower.includes('dark gothic') || nameLower.includes('dark-gothic')) {
    return 'dark-gothic';
  }
  if (nameLower.includes('rider-waite') || nameLower.includes('rider waite') || nameLower.includes('classic')) {
    return 'rider-waite';
  }
  return null;
}

// Helper to get background ID from background name
function getBackgroundIdFromName(backgroundName: string | null): string | null {
  if (!backgroundName) return null;
  const nameLower = backgroundName.toLowerCase();
  if (nameLower.includes('starry night')) return 'starry-night';
  if (nameLower.includes('aurora borealis')) return 'aurora-borealis';
  if (nameLower.includes('mystic forest')) return 'mystic-forest';
  return null;
}

// Shuffle deck with Fisher-Yates algorithm and assign random reversals
function shuffleDeckWithReversals(deck: TarotCardType[]): Array<{ 
  card: TarotCardType; 
  originalIndex: number; 
  isReversed: boolean 
}> {
  // Create array with original indices
  const indexedDeck = deck.map((card, originalIndex) => ({ card, originalIndex }));
  
  // Shuffle using Fisher-Yates
  const shuffled = [...indexedDeck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  // Random reversal percentage (0% - 100%)
  const reversalPercentage = Math.random();
  
  // Assign reversals based on percentage
  return shuffled.map(({ card, originalIndex }) => ({
    card,
    originalIndex,
    isReversed: Math.random() < reversalPercentage
  }));
}

export function CardDrawing({ spread, deck, onDrawingComplete, onBack }: CardDrawingProps) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawnCards, setDrawnCards] = useState<DrawnCard[]>([]);
  const [revealedCards, setRevealedCards] = useState<Set<number>>(new Set());
  const [currentStep, setCurrentStep] = useState<'ready' | 'drawing' | 'revealing' | 'complete'>('ready');
  
  // Fetch equipped decorations
  const { data: equippedDecorations, isLoading: isLoadingDecorations } = useEquippedDecorations();
  
  // Extract decoration settings
  const equippedDeckId = getDeckIdFromName(equippedDecorations?.cardImageDeck || null);
  const equippedCardBackPath = equippedDecorations?.cardBack?.filePath || null;
  const equippedBackgroundName = equippedDecorations?.background?.name || null;
  const backgroundId = getBackgroundIdFromName(equippedBackgroundName);
  
  // Get background URL if equipped
  const backgroundUrl = backgroundId ? getResponsiveBackgroundUrl(backgroundId, false) : null;
  
  const handleStartDrawing = async () => {
    setIsDrawing(true);
    setCurrentStep('drawing');
    
    // Simulate shuffling delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Shuffle deck with reversals
    const shuffled = shuffleDeckWithReversals(deck);
    
    // Automatically select first N cards from shuffled deck
    const selectedCards = shuffled.slice(0, spread.cardCount);
    
    // Create drawn cards from selected cards
    const cards: DrawnCard[] = selectedCards.map((shuffledCard, position) => {
      const spreadPosition = spread.positions[position];
      
      return {
        ...shuffledCard.card,
        position: spreadPosition.id,
        isReversed: shuffledCard.isReversed,
        positionMeaning: spreadPosition.description
      };
    });
    
    setDrawnCards(cards);
    setIsDrawing(false);
    
    // Move directly to revealing step
    setCurrentStep('revealing');
    
    // Auto-reveal cards one by one
    cards.forEach((_, index) => {
      setTimeout(() => {
        setRevealedCards(prev => new Set([...prev, index]));
        
        // If this is the last card, mark as complete
        if (index === cards.length - 1) {
          setTimeout(() => {
            setCurrentStep('complete');
          }, 500);
        }
      }, (index + 1) * 800);
    });
  };
  
  const handleRevealAll = () => {
    const allIndices = drawnCards.map((_, index) => index);
    setRevealedCards(new Set(allIndices));
    setCurrentStep('complete');
  };
  
  const handleComplete = () => {
    onDrawingComplete(drawnCards);
  };
  
  if (currentStep === 'ready') {
    return (
      <div className="text-center space-y-6">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">
            Draw Your Cards
          </h2>
          <p className="text-gray-400">
            Focus on your question and draw {spread.cardCount} {spread.cardCount === 1 ? 'card' : 'cards'} from the deck
          </p>
        </div>
        
        {/* Spread Info */}
        <div className="bg-dark-800 rounded-lg p-6 max-w-md mx-auto">
          <h3 className="text-lg font-semibold text-white mb-2">{spread.name}</h3>
          <p className="text-gray-400 text-sm mb-4">{spread.description}</p>
          <div className="text-primary-500 font-semibold">
            {spread.cardCount} {spread.cardCount === 1 ? 'Card' : 'Cards'}
          </div>
        </div>
        
        {/* Deck Visualization */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            {/* Stack of cards with shuffle animation */}
            {[0, 1, 2].map((offset) => (
              <div
                key={offset}
                className={`absolute w-24 h-42 ${isDrawing ? 'shuffle-animation' : ''}`}
                style={{
                  transform: `translate(${offset * 2}px, ${offset * -2}px)`,
                  zIndex: 3 - offset,
                  animationDelay: `${offset * 0.1}s`
                }}
              >
                <TarotCard
                  card={{
                    id: 'back',
                    name: 'Card Back',
                    arcana: 'major',
                    imagePath: '/images/cards/backs/default.webp',
                    keywords: [],
                    meanings: { upright: [], reversed: [] },
                    description: ''
                  }}
                  isRevealed={false}
                  size="md"
                  enableHoverEffects={!isDrawing}
                  equippedCardBackPath={equippedCardBackPath}
                />
              </div>
            ))}
          </div>
        </div>
        
        <div className="space-y-4">
          <Button
            size="lg"
            onClick={handleStartDrawing}
            disabled={isDrawing}
            className="px-8"
          >
            🎴 Draw Cards
          </Button>
          
          <Button
            variant="ghost"
            onClick={onBack}
            disabled={isDrawing}
          >
            ← Back to Spreads
          </Button>
        </div>
      </div>
    );
  }
  
  if (currentStep === 'drawing') {
    return (
      <div className="text-center space-y-6">
        <Loading size="lg" text="Shuffling the deck..." />
        <div className="space-y-2">
          <p className="text-white font-semibold">Drawing your cards...</p>
          <p className="text-gray-400 text-sm">
            The universe is selecting the perfect cards for your reading
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-2">
          Your Reading: {spread.name}
        </h2>
        <p className="text-gray-400">
          {currentStep === 'revealing' 
            ? 'Revealing your cards...' 
            : 'Your cards have been drawn!'
          }
        </p>
      </div>
      
      {/* Cards Layout */}
      <div 
        className="relative min-h-96 bg-dark-800 rounded-lg p-4 md:p-8 overflow-hidden"
        style={backgroundUrl ? {
          backgroundImage: `url(${backgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : undefined}
      >
        {/* Background overlay for readability */}
        {backgroundUrl && (
          <div className="absolute inset-0 bg-black/40 rounded-lg" />
        )}
        
        {spread.id === 'celtic-cross' ? (
          <CelticCrossLayout
            cards={drawnCards}
            spread={spread}
            revealedCards={revealedCards}
            showMeanings={currentStep === 'complete'}
            onCardClick={(index) => {
              if (currentStep === 'revealing') {
                setRevealedCards(prev => new Set([...prev, index]));
              }
            }}
            equippedDeckId={equippedDeckId}
            equippedCardBackPath={equippedCardBackPath}
          />
        ) : (
          <>
            {drawnCards.map((card, index) => {
              const position = spread.positions[index];
              const isRevealed = revealedCards.has(index);
              
              return (
                <div
                  key={`${card.id}-${index}`}
                  className="absolute transition-all duration-500"
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: 'translate(-50%, -50%)',
                    opacity: currentStep === 'revealing' && !isRevealed ? 0.3 : 1
                  }}
                >
                  <div className="text-center">
                    <TarotCard
                      card={card}
                      isRevealed={isRevealed}
                      size="md"
                      showMeaning={currentStep === 'complete'}
                      enableFlipAnimation={true}
                      animationDelay={index * 300}
                      position={index + 1}
                      spreadType={spread.id}
                      equippedDeckId={equippedDeckId}
                      equippedCardBackPath={equippedCardBackPath}
                      onClick={() => {
                        if (currentStep === 'revealing' && !isRevealed) {
                          setRevealedCards(prev => new Set([...prev, index]));
                        }
                      }}
                    />
                    <div className="mt-2 text-xs text-gray-400">
                      {position.name}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      
      {/* Controls */}
      <div className="flex justify-center space-x-4">
        {currentStep === 'revealing' && (
          <Button
            variant="secondary"
            onClick={handleRevealAll}
          >
            Reveal All Cards
          </Button>
        )}
        
        {currentStep === 'complete' && (
          <>
            <Button
              variant="secondary"
              onClick={onBack}
            >
              ← Draw Again
            </Button>
            <Button
              onClick={handleComplete}
            >
              Continue to Reading →
            </Button>
          </>
        )}
      </div>
      
      {/* Progress Indicator */}
      <div className="flex justify-center space-x-2">
        {drawnCards.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              revealedCards.has(index) ? 'bg-primary-500' : 'bg-dark-600'
            }`}
          />
        ))}
      </div>
    </div>
  );
}